import {
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  Link,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
} from '@mui/material';
import React, { useState, useEffect, useRef } from 'react';

import { ComponentMenuItem, SelectMenuProps } from './SelectMenu.props';
import { Label, MicroText, StyledStack } from './SelectMenu.styles';
import { SizeProps } from '../../../@types';
import { COLORS } from '../../../configs/colors';
import { dropdownVerifiedMessage } from '../../../utils/common';
import { SupplierLabel } from '../SupplierLabel/SupplierLabel';
import { Typography, TypographyVariantProps } from '../Typography';

// Inline sr-only style: keeps the label in the accessibility tree (so the Select is
// still named via aria-labelledby) while rendering nothing visible — used when the
// caller supplies an external visible label and only wants to drop the floating one.
const VISUALLY_HIDDEN = {
  border: 0,
  clip: 'rect(0 0 0 0)',
  height: '1px',
  margin: '-1px',
  overflow: 'hidden',
  padding: 0,
  position: 'absolute' as const,
  whiteSpace: 'nowrap' as const,
  width: '1px',
};

export const SelectMenu: React.FC<SelectMenuProps> = ({
  className,
  size = SizeProps.Small,
  id,
  label,
  items,
  minWidth = '100%',
  labelId,
  selectedValue,
  defaultSelectedValue,
  onChange,
  title,
  required,
  disabled,
  isFocused,
  readOnly,
  optionalLabelEnabled = false,
  helperTextEnabled = true,
  validate = false,
  optionalLabel,
  onOpen,
  hideInlineLabel = false,
  menuZIndex,
}) => {
  const selectRef = useRef<HTMLElement>(null);
  const [menuWidth, setMenuWidth] = useState<number | string>(minWidth);
  const [open, setOpen] = useState(false);
  const [internalHelperText, setHelperText] = useState<string>('');

  const trimmedLabel = label?.replaceAll('*', '');
  const optionalItem: ComponentMenuItem[] = [
    { value: '-1', label: optionalLabel || `Select ${trimmedLabel}` },
  ];
  const menuItems = optionalLabelEnabled ? optionalItem.concat(items) : items;

  useEffect(() => {
    if (
      (selectedValue?.toString() === '' || selectedValue?.toString() === '-1' || !selectedValue) &&
      validate
    ) {
      setHelperText(
        dropdownVerifiedMessage(
          selectedValue?.toString(),
          trimmedLabel ? `${trimmedLabel} field` : 'Field'
        )
      );
    } else {
      setHelperText('');
    }
  }, [selectedValue, validate, trimmedLabel]);

  const handleClose = () => {
    setOpen(false);
  };
  const handleOpen = () => {
    setOpen(true);

    if (typeof onOpen === 'function') {
      setTimeout(() => {
        onOpen();
      }, 0);
    }
  };

  useEffect(() => {
    setMenuWidth(selectRef.current?.clientWidth || minWidth);
    // Mount-only measurement of the rendered select. `minWidth` is intentionally
    // omitted: it is only the fallback for the first paint, and re-running on it
    // would clobber the measured width on every parent re-render that passes a
    // fresh value. `selectRef` is a stable ref object and never re-triggers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectRef]);

  useEffect(() => {
    isFocused && setOpen(isFocused);
  }, [isFocused]);

  // When the inline label is hidden we still associate a label via aria-labelledby
  // (MUI's combobox otherwise self-references and ends up with an empty name), so
  // fall back to an id derived from `id` when the caller didn't pass labelId.
  // Only wire the hidden-label association when there's actually a label to show;
  // otherwise fall back to the caller's labelId so we never point aria-labelledby
  // at an empty/absent element.
  const effectiveLabelId = hideInlineLabel && label ? (labelId ?? `${id}-label`) : labelId;

  return (
    <FormControl sx={{ minWidth }} size={size} error={!!internalHelperText} className={className}>
      {!hideInlineLabel ? (
        <InputLabel id={labelId}>{label}</InputLabel>
      ) : label ? (
        <InputLabel id={effectiveLabelId} sx={VISUALLY_HIDDEN}>
          {label}
        </InputLabel>
      ) : null}
      <Select
        open={open}
        onOpen={handleOpen}
        onClose={handleClose}
        // Left undefined unless opted in, so the 200-odd existing consumers keep
        // MUI's default menu layer untouched. See `menuZIndex` in the props.
        MenuProps={menuZIndex !== undefined ? { sx: { zIndex: menuZIndex } } : undefined}
        ref={selectRef}
        required={required}
        labelId={effectiveLabelId}
        id={id}
        value={selectedValue}
        defaultValue={defaultSelectedValue}
        autoWidth
        disabled={disabled}
        label={hideInlineLabel ? undefined : label}
        onChange={(event: SelectChangeEvent) => onChange && onChange(event.target.value)}
        renderValue={(value) => {
          // Loose equality is load-bearing: consumers pass numeric option values
          // while MUI hands the current selection back as a string.
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const item = menuItems.find((item: any) => item.value == value);

          if (item?.supplierLabel) {
            return <SupplierLabel supplierData={item?.supplierLabel} />;
          }

          if (typeof item?.label === 'string') {
            return (
              <Grid container sx={{ whiteSpace: 'normal' }}>
                <Label>{title}</Label>
                {item?.label || 'Not Selected'}
              </Grid>
            );
          } else {
            return <>{item?.label}</>;
          }
        }}
        readOnly={readOnly}
      >
        {menuItems.map(
          ({
            value,
            label,
            secondaryLabel,
            addressLabel,
            supplierLabel,
            navigateLink,
            disabled,
          }) => (
            <MenuItem
              style={{ width: menuWidth }}
              key={value}
              value={value}
              disabled={disabled}
              className={className}
              divider
              sx={{ whiteSpace: 'normal' }}
            >
              {value === 'addNewPricing' ? (
                <Link href={navigateLink} target="_blank" rel="noopener noreferrer">
                  {label}
                </Link>
              ) : secondaryLabel ? (
                <Stack>
                  <Typography>{label}</Typography>
                  <MicroText>{secondaryLabel}</MicroText>
                </Stack>
              ) : addressLabel ? (
                <StyledStack direction="column">
                  <Typography textColor={COLORS.Blue} variant={TypographyVariantProps.Subtitle2}>
                    {addressLabel.suburb}
                  </Typography>
                  <Typography textColor={COLORS.Gray} variant={TypographyVariantProps.Caption}>
                    {addressLabel.line1}
                  </Typography>
                  {addressLabel.line2 && (
                    <Typography textColor={COLORS.Gray} variant={TypographyVariantProps.Caption}>
                      {addressLabel.line2},
                    </Typography>
                  )}
                  <Typography textColor={COLORS.Gray} variant={TypographyVariantProps.Caption}>
                    {addressLabel.stateName},
                  </Typography>
                  <Typography textColor={COLORS.Gray} variant={TypographyVariantProps.Caption}>
                    {addressLabel.postCode},
                  </Typography>
                  <Typography textColor={COLORS.Gray} variant={TypographyVariantProps.Caption}>
                    {addressLabel.countryName}
                  </Typography>
                  <MicroText>Address Type: {addressLabel.addressType}</MicroText>
                </StyledStack>
              ) : supplierLabel ? (
                <SupplierLabel supplierData={supplierLabel} />
              ) : (
                <Typography>{label}</Typography>
              )}
            </MenuItem>
          )
        )}
      </Select>
      {internalHelperText && helperTextEnabled && (
        <FormHelperText>{internalHelperText}</FormHelperText>
      )}
    </FormControl>
  );
};
